// Pure filtering logic for the Expiring Credentials dashboard, extracted so
// it's unit-testable independent of the page's rendering.

export const EXPIRY_PRESETS = [
  { value: 'all', label: 'Any time' },
  { value: 'expired', label: 'Expired' },
  { value: '7', label: 'Next 7 days' },
  { value: '30', label: 'Next 30 days' },
  { value: '60', label: 'Next 60 days' },
  { value: '90', label: 'Next 90 days' },
  { value: 'custom', label: 'Custom range…' },
];

export const emptyExpiringCredentialsFilters = {
  candidate: '',
  inHyredocs: 'all',
  placement: '',
  credential: 'all',
  lastRequest: 'all',
  expiryPreset: 'all',
  expiryFrom: '',
  expiryTo: '',
};

// Row shape (from GET /api/compliance/expiring): { candidate_name,
// candidate_email, in_hyredocs, placements: [{id, ...}], category_name,
// date_expiration (YYYY-MM-DD), days_until_expiry, last_request: {status} | null }
export function matchesExpiringCredentialFilters(row, filters) {
  const candidateQuery = (filters.candidate || '').trim().toLowerCase();
  if (candidateQuery) {
    // Match each typed word independently against name+email combined, so a
    // partial/incomplete query like "and bex" still finds "Andrew Bexon" —
    // matching the literal query as one substring would miss the "rew"/"on"
    // gaps and wrongly report no results.
    const haystack = `${row.candidate_name || ''} ${row.candidate_email || ''}`.toLowerCase();
    const tokens = candidateQuery.split(/\s+/).filter(Boolean);
    const matches = tokens.every((token) => haystack.includes(token));
    if (!matches) return false;
  }

  if (filters.inHyredocs !== 'all') {
    const want = filters.inHyredocs === 'yes';
    if (!!row.in_hyredocs !== want) return false;
  }

  const placementQuery = (filters.placement || '').trim().toLowerCase();
  if (placementQuery) {
    const matches = (row.placements || []).some((p) => String(p.id).toLowerCase().includes(placementQuery));
    if (!matches) return false;
  }

  if (filters.credential !== 'all' && row.category_name !== filters.credential) return false;

  if (filters.lastRequest !== 'all') {
    if (filters.lastRequest === 'not_requested') {
      if (row.last_request) return false;
    } else if (!row.last_request || row.last_request.status !== filters.lastRequest) {
      return false;
    }
  }

  if (filters.expiryPreset === 'expired' && row.days_until_expiry >= 0) return false;
  if (['7', '30', '60', '90'].includes(filters.expiryPreset)) {
    const max = Number(filters.expiryPreset);
    if (!(row.days_until_expiry >= 0 && row.days_until_expiry <= max)) return false;
  }
  if (filters.expiryPreset === 'custom') {
    // date_expiration is YYYY-MM-DD — plain string comparison sorts
    // chronologically, same as the date inputs' own native value format.
    if (filters.expiryFrom && row.date_expiration < filters.expiryFrom) return false;
    if (filters.expiryTo && row.date_expiration > filters.expiryTo) return false;
  }

  return true;
}

export function filterExpiringCredentials(rows, filters) {
  return (rows || []).filter((row) => matchesExpiringCredentialFilters(row, filters));
}

const compare = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

const SORT_ACCESSORS = {
  candidate: (row) => (row.candidate_name || '').toLowerCase(),
  inHyredocs: (row) => (row.in_hyredocs ? 1 : 0),
  placement: (row) => {
    const id = row.placements?.[0]?.id;
    const n = Number(id);
    return Number.isFinite(n) ? n : Infinity;
  },
  credential: (row) => (row.category_name || '').toLowerCase(),
  expiry: (row) => row.days_until_expiry,
  // not_requested < sent < opened — least-engaged first, ascending.
  lastRequest: (row) => (row.last_request ? (row.last_request.status === 'opened' ? 2 : 1) : 0),
};

export const SORTABLE_COLUMNS = Object.keys(SORT_ACCESSORS);

// Stable sort: ties fall back to candidate name so re-sorting a column with
// many equal values (e.g. every "In HyreDocs: No" row) doesn't reshuffle
// rows that were already adjacent.
export function sortExpiringCredentials(rows, sortKey, sortDir) {
  const list = rows || [];
  const accessor = SORT_ACCESSORS[sortKey];
  if (!accessor) return list;
  const dir = sortDir === 'desc' ? -1 : 1;
  const nameAccessor = SORT_ACCESSORS.candidate;
  return [...list].sort((a, b) => {
    const primary = compare(accessor(a), accessor(b));
    if (primary !== 0) return primary * dir;
    return compare(nameAccessor(a), nameAccessor(b));
  });
}

export const EXPIRING_CREDENTIALS_CSV_HEADERS = [
  'Candidate Name', 'Candidate Email', 'In HyreDocs', 'Placement ID(s)',
  'Credential', 'Expiry Date', 'Days Until Expiry', 'Last Request Status',
  'Last Request Sent', 'Last Reminder Sent',
];

// RFC 4180: a field needs quoting if it contains a comma, a double quote, or
// a line break; an embedded double quote is escaped by doubling it.
function csvCell(value) {
  const str = value == null ? '' : String(value);
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

function csvRow(cells) {
  return cells.map(csvCell).join(',');
}

const lastRequestStatusLabel = (row) => {
  if (!row.last_request) return 'Not yet requested';
  return row.last_request.status === 'opened' ? 'Opened' : 'Sent';
};

// Exports exactly the rows passed in (the caller's already-filtered/sorted
// list), one row per credential — matching what's on screen, not the full
// unfiltered dataset, so "export" means "export what I'm looking at".
export function expiringCredentialsToCsv(rows) {
  const lines = [csvRow(EXPIRING_CREDENTIALS_CSV_HEADERS)];
  for (const row of rows || []) {
    lines.push(csvRow([
      row.candidate_name || '',
      row.candidate_email || '',
      row.in_hyredocs ? 'Yes' : 'No',
      (row.placements || []).map((p) => p.id).join('; '),
      row.category_name || '',
      row.date_expiration || '',
      row.days_until_expiry,
      lastRequestStatusLabel(row),
      row.last_request?.sent_at || '',
      row.last_request?.last_reminder_at || '',
    ]));
  }
  // CRLF is the RFC 4180 line ending and what Excel expects to reliably
  // treat every row as a row rather than sometimes joining lines.
  return lines.join('\r\n');
}
